<template>
  <section class="gce">
    <header class="gce__head">
      <h2 class="gce__title">{{ $t("generalEvents.title") }}</h2>
      <p class="gce__sub">
        {{ $t("generalEvents.sub") }}
      </p>
    </header>

    <p v-if="!rows.length" class="gce__empty">
      {{ $t("generalEvents.empty") }}
    </p>

    <ul v-else class="gce__list">
      <li
        v-for="row in rows"
        :key="row.generalClassId"
        class="gce__row"
        :class="{
          'gce__row--declined': row.declined,
          'gce__row--confirmed': row.going,
        }"
      >
        <div class="gce__when">
          <span class="gce__day">{{ row.dayLabel }}</span>
          <span class="gce__hour">{{ row.hourLabel }}</span>
        </div>

        <div class="gce__info">
          <span
            class="gce__type"
            :class="
              row.generalClassType === 'mentoring'
                ? 'gce__type--mentoring'
                : row.generalClassType === 'student_event'
                ? 'gce__type--event'
                : 'gce__type--class'
            "
          >
            {{ row.typeLabel }}
          </span>
          <div class="gce__name">{{ row.title }}</div>
          <div class="gce__meta">
            <span v-if="row.teacherName">
              <v-icon size="14">mdi-account-tie-outline</v-icon>
              {{ row.teacherName }}
            </span>
            <span v-if="row.audienceLabel">
              <v-icon size="14">mdi-signal-cellular-2</v-icon>
              {{ row.audienceLabel }}
            </span>
          </div>
          <div v-if="row.declined" class="gce__state">
            {{ $t("generalEvents.declined") }}
          </div>
          <div v-else-if="row.going" class="gce__state gce__state--ok">
            <v-icon size="14" color="#00A379">mdi-check</v-icon>
            {{
              row.addedByTeacher
                ? $t("generalEvents.addedByTeacher")
                : $t("generalEvents.confirmed")
            }}
          </div>
        </div>

        <div class="gce__actions">
          <button
            v-if="!row.going && !row.started"
            type="button"
            class="gce__btn gce__btn--solid"
            :disabled="busyId === row.generalClassId"
            @click="$emit('participate', row.generalClassId)"
          >
            {{ $t("generalEvents.participate") }}
          </button>
          <button
            v-if="!row.declined"
            type="button"
            class="gce__btn gce__btn--ghost"
            :disabled="busyId === row.generalClassId"
            @click="$emit('decline', row.generalClassId)"
          >
            {{ $t("generalEvents.decline") }}
          </button>
          <button
            type="button"
            class="gce__btn"
            :disabled="!row.materialUrl"
            :title="row.materialUrl ? '' : $t('generalEvents.noMaterial')"
            @click="open(row.materialUrl)"
          >
            <v-icon size="16">mdi-book-open-variant</v-icon>
            {{ $t("generalEvents.material") }}
          </button>
          <!-- O link da sala só abre para quem confirmou: era clicando aqui,
               sem querer, que o aluno caía na conversação em vez da própria
               aula com o professor. -->
          <button
            type="button"
            class="gce__btn"
            :disabled="!row.going || !row.link"
            :title="row.going ? '' : $t('generalEvents.confirmToJoin')"
            @click="open(row.link)"
          >
            <v-icon size="16">mdi-video-outline</v-icon>
            {{ $t("generalEvents.lesson") }}
          </button>
        </div>
      </li>
    </ul>
  </section>
</template>

<script>
import { weekDayShortLabel } from "@/shared/classPresentation";

function parseDate(value) {
  if (!value) return null;
  const date = new Date(String(value).replace(" ", "T"));
  return Number.isNaN(date.getTime()) ? null : date;
}

function pad(value) {
  return String(value).padStart(2, "0");
}

export default {
  name: "GeneralClassEventsList",
  props: {
    // Itens de aula geral como a API os devolve (toCustomerCalendarEvent),
    // incluindo os recusados.
    items: { type: Array, default: () => [] },
    busyId: { type: String, default: null },
  },
  computed: {
    rows() {
      const now = new Date();

      return this.items
        .map((item) => ({ item, start: parseDate(item.start) }))
        .filter(({ item, start }) => {
          const end = parseDate(item.end) || start;
          // Só o que ainda vai acontecer (ou está acontecendo). O histórico de
          // presença fica na aba Conversação do perfil.
          return start && end >= now;
        })
        .sort((a, b) => a.start - b.start)
        .map(({ item, start }) => {
          const end = parseDate(item.end);
          const declined = item.participationStatus === "DECLINED";
          const going =
            !declined &&
            (!!item.participationConfirmed || !!item.addedByTeacher);

          return {
            generalClassId: item.generalClassId,
            generalClassType: item.generalClassType,
            typeLabel:
              item.generalClassType === "mentoring"
                ? this.$t("generalEvents.mentoring")
                : item.generalClassType === "student_event"
                ? this.$t("generalEvents.event")
                : this.$t("generalEvents.conversationClass"),
            title: this.titleOf(item),
            teacherName: item.teacherName,
            audienceLabel: item.audienceLabel,
            dayLabel: this.$t("formats.weekdayDayMonth", {
              weekday: weekDayShortLabel(start.getDay()),
              day: pad(start.getDate()),
              month: pad(start.getMonth() + 1),
            }),
            hourLabel: end
              ? `${pad(start.getHours())}:${pad(start.getMinutes())} – ${pad(
                  end.getHours()
                )}:${pad(end.getMinutes())}`
              : `${pad(start.getHours())}:${pad(start.getMinutes())}`,
            declined,
            going,
            // A confirmação vale até o horário de início; depois disso a API
            // recusa, então o botão some.
            started: start <= now,
            addedByTeacher: !!item.addedByTeacher,
            link: item.link || null,
            materialUrl: this.materialUrlOf(item),
          };
        });
    },
  },
  methods: {
    // A API monta o nome como "<tipo> - <título>"; o tipo já tem o selo dele.
    titleOf(item) {
      const name = String(item.name || "");
      const prefix = `${item.generalClassTypeLabel} - `;
      return item.generalClassTypeLabel && name.startsWith(prefix)
        ? name.slice(prefix.length)
        : name;
    },
    // Mesma regra do diálogo do calendário: `content` é "módulo/material" do
    // curso; na falta dele vale o link externo cadastrado na aula.
    materialUrlOf(item) {
      const [moduleId, materialId] = String(item.content || "")
        .trim()
        .split("/");

      if (moduleId) {
        const params = new URLSearchParams();
        if (materialId) params.set("material", materialId);
        const query = params.toString();
        const base = `${window.location.origin}/connect/aluno/curso/${moduleId}`;
        return query ? `${base}?${query}` : base;
      }

      return item.materialLink || null;
    },
    open(url) {
      if (url) window.open(url, "_blank");
    },
  },
};
</script>

<style scoped>
.gce {
  --ink: var(--dk-ink, #231a2e);
  --muted: var(--dk-muted, #6b6472);
  --line: rgba(var(--dk-ink-rgb, 35, 26, 46), 0.09);
  --accent: var(--dk-pink, #e8276f);
  margin-top: 28px;
  color: var(--ink);
}
.gce__title {
  margin: 0;
  font-family: "Poppins", sans-serif;
  font-size: 1.35rem;
  font-weight: 800;
}
.gce__sub,
.gce__empty {
  margin: 6px 0 0;
  font-size: 0.86rem;
  color: var(--muted);
}
.gce__empty {
  margin-top: 16px;
}
.gce__list {
  list-style: none;
  padding: 0;
  margin: 14px 0 0;
  border: 1px solid var(--line);
  border-radius: 14px;
  background: var(--dk-surface, #fff);
  overflow: hidden;
}
.gce__row {
  display: grid;
  grid-template-columns: 110px 1fr auto;
  gap: 16px;
  align-items: center;
  padding: 14px 16px;
  border-top: 1px solid var(--line);
  transition: background 0.15s, opacity 0.15s;
}
.gce__row:first-child {
  border-top: none;
}
.gce__row--confirmed {
  background: rgba(0, 163, 121, 0.05);
}
/* Recusa: linha cinza, mas os botões seguem ativos para voltar atrás. */
.gce__row--declined {
  background: #f1f0f2;
}
.gce__row--declined .gce__when,
.gce__row--declined .gce__info {
  opacity: 0.5;
}
.gce__when {
  display: flex;
  flex-direction: column;
}
.gce__day {
  font-weight: 700;
  font-size: 0.9rem;
}
.gce__hour {
  font-size: 0.82rem;
  color: var(--muted);
}
.gce__type {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.gce__type--class {
  background: rgba(168, 38, 225, 0.12);
  color: #7a1ca4;
}
.gce__type--mentoring {
  background: rgba(255, 43, 122, 0.12);
  color: #c2185b;
}
/* Card 17trt6jcypk */
.gce__type--event {
  background: rgba(255, 93, 42, 0.12);
  color: #c2410c;
}
.gce__name {
  margin-top: 4px;
  font-weight: 700;
}
.gce__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 2px;
  font-size: 0.8rem;
  color: var(--muted);
}
.gce__state {
  margin-top: 4px;
  font-size: 0.78rem;
  color: var(--muted);
}
.gce__state--ok {
  color: #00795a;
}
.gce__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}
.gce__btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border: 1px solid var(--line);
  border-radius: 999px;
  background: var(--dk-surface, #fff);
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--ink);
  cursor: pointer;
}
.gce__btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.gce__btn--solid {
  background: var(--accent);
  border-color: var(--accent);
  color: #fff;
}
.gce__btn--ghost {
  color: var(--muted);
}
@media (max-width: 760px) {
  .gce__row {
    grid-template-columns: 1fr;
    gap: 8px;
  }
  .gce__actions {
    justify-content: flex-start;
  }
}
</style>
